import { useState, useEffect, useCallback } from 'react';
import { GameData, ProofRecord } from '../types/game';
import { World, Level, WorldsManifest } from '../types/world';
import { GameStorage } from '../services/storage';
import { loadWorld } from '../services/worldLoader';
import { checkAchievements } from '../services/achievementEngine';
import { makeLevelKey, matchesLevelKey } from '../utils/ids';
import { useLecturerAuth } from '../context/LecturerAuthContext';

export function useGameState() {
  const { isAuthenticated } = useLecturerAuth();
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [worlds, setWorlds] = useState<Map<string, World>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load game data
    const data = GameStorage.load() || GameStorage.createEmpty();
    setGameData(data);
    setLoading(false);
  }, []);

  const saveGameData = useCallback((data: GameData) => {
    setGameData(data);
    GameStorage.save(data);
  }, []);

  const getLevel = useCallback((worldId: string, levelId: string): Level | null => {
    const world = worlds.get(worldId);
    if (!world) return null;
    return world.levels.find(l => l.id === levelId) || null;
  }, [worlds]);

  const loadWorldData = useCallback(async (worldId: string, manifest?: WorldsManifest) => {
    const world = await loadWorld(worldId, manifest);
    if (world) {
      setWorlds(prev => new Map(prev).set(worldId, world));
    }
    return world;
  }, []);

  const completeLevel = useCallback((worldId: string, levelId: string) => {
    if (!gameData) return;
    
    const updated = { ...gameData };
    const compositeId = makeLevelKey(worldId, levelId);
    if (!updated.progress.completedLevels.includes(compositeId)) {
      updated.progress.completedLevels.push(compositeId);
    }
    
    // Check for newly unlocked achievements
    const newAchievements = checkAchievements(updated);
    newAchievements.forEach(achId => {
      updated.achievements[achId] = {
        id: achId,
        name: '',
        description: '',
        icon: '',
        unlockedAt: new Date().toISOString(),
      };
    });
    
    saveGameData(updated);
  }, [gameData, saveGameData]);

  const saveProof = useCallback((worldId: string, levelId: string, proof: ProofRecord) => {
    if (!gameData) {
      console.warn('⚠️ Cannot save proof: gameData is null');
      return;
    }
    
    console.log('💾 saveProof called:', { levelId, proof: { ...proof, code: proof.code?.substring(0, 50) + '...' } });
    
    const updated = { ...gameData };
    // Ensure proofs object exists
    if (!updated.proofs) {
      updated.proofs = {};
    }
    const compositeId = makeLevelKey(worldId, levelId);
    updated.proofs[compositeId] = proof;
    
    // Update XP
    if (proof.correct) {
      let xpGain = proof.hintsUsed === 0 ? 150 : 100;
      if (proof.attempts === 1) xpGain += 25;
      if (proof.timeSpent < 300) xpGain += 25;
      
      updated.progress.xp += xpGain;
      updated.progress.level = Math.floor(updated.progress.xp / 500) + 1;
    }
    
    // Unlock tactics
    const level = worlds.get(worldId)?.levels.find(l => l.id === levelId);
    
    if (level?.unlockedTactics && proof.correct) {
      level.unlockedTactics.forEach(tactic => {
        if (!updated.unlockedTactics.includes(tactic)) {
          updated.unlockedTactics.push(tactic);
        }
      });
    }
    
    console.log('💾 Calling saveGameData with updated proofs:', Object.keys(updated.proofs));
    saveGameData(updated);
  }, [gameData, worlds, saveGameData]);

  const isLevelCompleted = useCallback((worldId: string, levelId: string): boolean => {
    if (!gameData) return false;
    makeLevelKey(worldId, levelId);
    // const compositeId = makeLevelKey(worldId, levelId);
    return gameData.progress.completedLevels.some(id => matchesLevelKey(id, worldId, levelId));
  }, [gameData]);

  const isLecturer = useCallback((): boolean => {
    return isAuthenticated;
  }, [isAuthenticated]);

  const isLevelUnlocked = useCallback((worldId: string, level: Level, levels: Level[], completedLevels: string[]): boolean => {
    if (!gameData) return false;
    // Allow browsing every level without changing completion or lecturer mode.
    if (isLecturer() || gameData.settings.showAllLevels) return true;
    
    const levelIndex = levels.findIndex(l => l.id === level.id);
    if (levelIndex === 0) return true; // First level is always unlocked
    
    // Check prerequisites
    if (level.prerequisites && level.prerequisites.length > 0) {
      return level.prerequisites.every(prereqId => completedLevels.includes(makeLevelKey(worldId, prereqId)));
    }
    
    // Otherwise, previous level must be completed
    if (levelIndex > 0) {
      const previousLevel = levels[levelIndex - 1];
      return completedLevels.includes(makeLevelKey(worldId, previousLevel.id));
    }
    
    return false;
  }, [gameData, isLecturer]);

  const isWorldUnlocked = useCallback((worldId: string, world: World): boolean => {
    if (!gameData) return worldId === 'world0';
    // All worlds must be accessible when browsing every level.
    if (isLecturer() || gameData.settings.showAllLevels) return true;
    
    if (world.prerequisites && world.prerequisites.length > 0) {
      return world.prerequisites.every(prereq => {
        const prereqWorld = worlds.get(prereq);
        if (!prereqWorld) return false;
        return prereqWorld.levels.every(level => 
          gameData.progress.completedLevels.some(id => matchesLevelKey(id, prereqWorld.id, level.id))
        );
      });
    }
    return true;
  }, [gameData, worlds, isLecturer]);

  return {
    gameData,
    loading,
    worlds,
    getLevel,
    loadWorldData,
    completeLevel,
    saveProof,
    isLevelCompleted,
    isLevelUnlocked,
    isWorldUnlocked,
    isLecturer,
    saveGameData,
  };
}
